import { useMemo } from 'react';

// deep import (not the feature barrel): the barrel's components reach back into
// the wallet feature, the store does not.
import { useFlowStore } from '@/features/flow/store';
import { createV1Backend, type SpeedBackend } from '@/lib/api/speed';

import { useAuthedApi, useFactionsAuthedApi } from './useAuthedApi';
import { useV2Backend } from './v2BackendHook';

/**
 * The speed-trading backend for the active flow, or `null` while there is none.
 *
 * V1 binds the existing service functions to the two ky clients (`createV1Backend`), memoized on
 * them, so it is `null` exactly when they are, which is exactly when there is no token: callers
 * that gate on `backend !== null` behave as they did gating on the client. V2 is the browser
 * wallet's backend (`useV2Backend`). Both branch hooks always run (rules of hooks); the active
 * flow only picks which result is returned, and anything but `'v2'` (including the moment a
 * social identity is still being resolved) is V1, exactly today's path.
 */
export function useSpeedBackend(): SpeedBackend | null {
    const flow = useFlowStore((s) => s.active);
    const authedApi = useAuthedApi();
    const factionsApi = useFactionsAuthedApi();
    const v1 = useMemo(
        () =>
            authedApi && factionsApi
                ? createV1Backend(authedApi, factionsApi)
                : null,
        [authedApi, factionsApi],
    );
    const v2 = useV2Backend();

    return flow === 'v2' ? v2 : v1;
}
